import { useCallback } from 'react'
import { storage } from '@/utils/storage'
import { useAuth } from './useAuth'

// 受給者編集画面の「一時保存（下書き）」をこの端末の localStorage へ保持する。
// スコープ = ユーザ(cognitoSub) × 受給者。共有 PC で他ユーザの下書きが見えないようにする。
//  - 編集モード: scope = 受給者ID（受給者ごとに 1 件）。
//  - 新規モード: まだ主キーが無いため scope = `new:{合成ID}`。1 ユーザで複数の未完成下書きを併存させる。
// 注意: あくまで端末ローカル保存。別端末・別ブラウザには引き継がれない（前端方案の制約）。
const DRAFT_KEY_PREFIX = 'wbms:recipient-draft'
const DRAFT_VERSION = 2 as const
const NEW_SCOPE_PREFIX = 'new:'

export interface RecipientDraftPayload {
  comment: string
  deathInfo: string
  nameField: string
  zipField: string
  addressField: string
  bankCode: string
  bankBranchCode: string
  bankAccountType: string
  bankAccountNumber: string
  bankHolder: string
  isPlaintiff: boolean
  requiresConsideration: boolean
  hideInstructionText: boolean
  detailRows: Record<string, string>[]
  detailRowIds: (number | null)[]
  detailReviewed?: boolean[]
  detailReviewMemos?: (string | null)[]
}

export interface RecipientDraft extends RecipientDraftPayload {
  v: typeof DRAFT_VERSION
  /** ISO 文字列。表示は formatDateTime で整形する。 */
  savedAt: string
  /**
   * 下書き作成時点で参照していたサーバ側レコードの最終更新日時(RecipientResponse.updatedAt)。
   * 復元時・保存時に現在のサーバ値と突き合わせ、編集中に他の利用者が更新していないか判定する。
   * 新規モードは対応するサーバレコードが無いため null。
   */
  serverUpdatedAt?: string | null
}

// 新規モードの未完成下書き 1 件分（復元バナーの一覧表示に使う）。
export interface NewDraftEntry {
  /** scope 文字列（`new:{合成ID}`）。復元時にアクティブ scope として使う。 */
  scope: string
  savedAt: string
  draft: RecipientDraft
}

export interface UseRecipientDraftResult {
  load: () => RecipientDraft | null
  save: (payload: RecipientDraftPayload, serverUpdatedAt?: string | null) => string | null
  clear: () => void
  /** この端末・このユーザの新規下書きを新しい順に列挙する。 */
  listNew: () => NewDraftEntry[]
  /** 任意 scope の下書きを削除する（一覧からの個別「破棄」用）。 */
  removeScope: (scope: string) => void
}

// 新規モード用の新しい scope を採番する。`new:{UUID}`。
export function newDraftScope(): string {
  const id =
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`
  return `${NEW_SCOPE_PREFIX}${id}`
}

// scope = 受給者ID（編集モード）/ `new:{合成ID}`（新規モード）/ null（無効）。
export type RecipientDraftScope = number | string | null

export function useRecipientDraft(scope: RecipientDraftScope): UseRecipientDraftResult {
  const { session } = useAuth()
  const sub = session?.cognitoSub ?? 'anon'
  const base = `${DRAFT_KEY_PREFIX}:${sub}:`
  const key = scope != null ? `${base}${scope}` : null

  const load = useCallback((): RecipientDraft | null => {
    if (!key) return null
    const draft = storage.get<RecipientDraft>(key)
    // バージョン不一致の古い下書きは無視する（フォーマット変更時の事故防止）。
    if (!draft || draft.v !== DRAFT_VERSION) return null
    return draft
  }, [key])

  // 保存した savedAt(ISO)を返す。key 無し・容量超過などで保存できなかった場合は null。
  // serverUpdatedAt: 下書き作成時点のサーバ最終更新日時(競合検出用)。新規は null。
  const save = useCallback((payload: RecipientDraftPayload, serverUpdatedAt: string | null = null): string | null => {
    if (!key) return null
    const savedAt = new Date().toISOString()
    const ok = storage.set(key, { v: DRAFT_VERSION, savedAt, serverUpdatedAt, ...payload })
    return ok ? savedAt : null
  }, [key])

  const clear = useCallback((): void => {
    if (key) storage.remove(key)
  }, [key])

  const listNew = useCallback((): NewDraftEntry[] => {
    const newKeyPrefix = `${base}${NEW_SCOPE_PREFIX}`
    const entries: NewDraftEntry[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (!k || !k.startsWith(newKeyPrefix)) continue
      const draft = storage.get<RecipientDraft>(k)
      if (!draft || draft.v !== DRAFT_VERSION) continue
      entries.push({ scope: k.slice(base.length), savedAt: draft.savedAt, draft })
    }
    // 新しい順（ISO 文字列はそのまま辞書順で時系列になる）。
    return entries.sort((a, b) => b.savedAt.localeCompare(a.savedAt))
  }, [base])

  const removeScope = useCallback((s: string): void => {
    storage.remove(`${base}${s}`)
  }, [base])

  return { load, save, clear, listNew, removeScope }
}
